// Glassmorphic color palette for all notes in the application
// This ensures consistency between new notes, branch notes, and any other auto-generated notes
// Uses semi-transparent backgrounds (0.8 opacity) with corresponding border colors

export interface GlassmorphicColor {
  id: string;
  name: string;
  bg: string; // Semi-transparent background (0.8 opacity)
  border: string; // Corresponding border color (darker shade)
  solid: string; // Solid version for fallback
}

export const GLASSMORPHIC_COLORS: GlassmorphicColor[] = [
  {
    id: 'rose-glass',
    name: 'Rose Glass',
    bg: 'rgba(255, 228, 230, 0.8)', // Semi-transparent rose
    border: '#FECACA', // red-200
    solid: '#FFEBEE'
  },
  {
    id: 'blue-glass',
    name: 'Blue Glass', 
    bg: 'rgba(219, 234, 254, 0.8)', // Semi-transparent blue
    border: '#BFDBFE', // blue-200
    solid: '#E3F2FD'
  },
  {
    id: 'green-glass',
    name: 'Green Glass',
    bg: 'rgba(220, 252, 231, 0.8)', // Semi-transparent green
    border: '#BBF7D0', // green-200
    solid: '#E8F5E9'
  },
  {
    id: 'purple-glass',
    name: 'Purple Glass',
    bg: 'rgba(243, 232, 255, 0.8)', // Semi-transparent purple
    border: '#E9D5FF', // purple-200
    solid: '#F3E5F5'
  },
  {
    id: 'yellow-glass',
    name: 'Yellow Glass',
    bg: 'rgba(254, 253, 231, 0.8)', // Semi-transparent yellow
    border: '#FEF3C7', // yellow-200
    solid: '#FFFDE7'
  },
  {
    id: 'orange-glass',
    name: 'Orange Glass',
    bg: 'rgba(255, 243, 224, 0.8)', // Semi-transparent orange
    border: '#FED7AA', // orange-200
    solid: '#FFF3E0'
  },
  {
    id: 'teal-glass',
    name: 'Teal Glass',
    bg: 'rgba(224, 242, 241, 0.8)', // Semi-transparent teal
    border: '#99F6E4', // teal-200
    solid: '#E0F2F1'
  },
  {
    id: 'pink-glass',
    name: 'Pink Glass',
    bg: 'rgba(252, 228, 236, 0.8)', // Semi-transparent pink
    border: '#FBCFE8', // pink-200
    solid: '#FCE4EC'
  },
  {
    id: 'indigo-glass',
    name: 'Indigo Glass',
    bg: 'rgba(232, 234, 246, 0.8)', // Semi-transparent indigo
    border: '#C7D2FE', // indigo-200
    solid: '#E8EAF6'
  },
  {
    id: 'cyan-glass',
    name: 'Cyan Glass',
    bg: 'rgba(224, 247, 250, 0.8)', // Semi-transparent cyan
    border: '#A5F3FC', // cyan-200
    solid: '#E0F7FA'
  },
  {
    id: 'lime-glass',
    name: 'Lime Glass',
    bg: 'rgba(249, 251, 231, 0.8)', // Semi-transparent lime
    border: '#FDE68A', // lime-200
    solid: '#F9FBE7'
  },
  {
    id: 'white-glass',
    name: 'White Glass',
    bg: 'rgba(255, 255, 255, 0.8)', // Semi-transparent white
    border: '#E5E7EB', // gray-200
    solid: '#FFFFFF'
  }
];

// Legacy support - keep the old solid colors for backward compatibility
export const NOTE_COLORS = GLASSMORPHIC_COLORS.map(color => color.solid);

/**
 * Get a random glassmorphic color that is different from the parent color
 * @param parentColor - The solid color of the parent note to avoid
 * @returns A glassmorphic color that is different from the parent
 */
export function getRandomGlassmorphicColorExcluding(parentColor: string): GlassmorphicColor {
  const availableColors = GLASSMORPHIC_COLORS.filter(color => color.solid !== parentColor);
  
  if (availableColors.length === 0) {
    // Fallback to first color if somehow all colors are the same as parent
    return GLASSMORPHIC_COLORS[0];
  }
  
  const randomIndex = Math.floor(Math.random() * availableColors.length);
  return availableColors[randomIndex];
}

/**
 * Get a random glassmorphic color
 * @returns A random glassmorphic color
 */
export function getRandomGlassmorphicColor(): GlassmorphicColor {
  const randomIndex = Math.floor(Math.random() * GLASSMORPHIC_COLORS.length);
  return GLASSMORPHIC_COLORS[randomIndex];
}

/**
 * Get glassmorphic color by solid color
 * @param solidColor - The solid color to find glassmorphic version for
 * @returns The corresponding glassmorphic color or first one as fallback
 */
export function getGlassmorphicColorBySolid(solidColor: string): GlassmorphicColor {
  return GLASSMORPHIC_COLORS.find(color => color.solid === solidColor) || GLASSMORPHIC_COLORS[0];
}

/**
 * Get a random color from the palette that is different from the parent color
 * @param parentColor - The color of the parent note to avoid
 * @returns A color from the palette that is different from the parent
 */
export function getRandomColorExcluding(parentColor: string): string {
  const availableColors = NOTE_COLORS.filter(color => color !== parentColor);
  
  if (availableColors.length === 0) {
    // Fallback to first color if somehow all colors are the same as parent
    return NOTE_COLORS[0];
  }
  
  const randomIndex = Math.floor(Math.random() * availableColors.length);
  return availableColors[randomIndex];
}

/**
 * Get a random color from the palette
 * @returns A random color from the palette
 */
export function getRandomColor(): string {
  const randomIndex = Math.floor(Math.random() * NOTE_COLORS.length);
  return NOTE_COLORS[randomIndex];
}

/**
 * Check if a color is in the official palette
 * @param color - The color to check
 * @returns True if the color is in the palette
 */
export function isColorInPalette(color: string): boolean {
  return NOTE_COLORS.includes(color);
}
